import { ClaimSums } from 'src/types/claim-sum';
import { z } from 'zod';
import { COFACE_FORM_NAME } from './form.factory';


export const debtorSchema = z.object({
  id: z.string().nullish(),
  jurisdiction: z.string().length(2),
  debtor_type: z.enum(['org', 'ind']),
  party_type_id: z.string().nullish(),
  name: z.string().nullable(),
  tax_number: z.string().nullish(),
  registration_number: z.string().nullish(),
  address: z.object({
    country_iso: z.string().length(2).nullish(),
    postcode: z.string().nullable(),
    settlement: z.string().nullable(),
    street: z.string().nullable(),
  }).nullish(),
  representative: z.object({
    name: z.string().nullable(),
    address: z.object({
      country_iso: z.string().nullable(),
      postcode: z.string().nullable(),
      settlement: z.string().nullable(),
      street: z.string().nullable(),
    }),
  }).nullish(),
  is_editable: z.boolean(),
});
export type Debtor = z.infer<typeof debtorSchema>;

const claimSchema = z.object({
  id: z.string(),
  file: z.object({
    file_name: z.string(),
    storage_url: z.string(),
  }).or(z.instanceof(File)).nullish(),
  original_amount: z.number(),
  currency_iso: z.string().length(3),
  due_date_at: z.union([z.string(), z.date()]).nullish().catch(null),
  issued_at: z.string().nullish().catch(null),
  invoice_number: z.string().nullish().catch(null),
  coface_invoice_type_code: z.string().nullish().catch(null),
  claim_type_internal_id: z.string().nullish().catch(null),
  is_editable: z.boolean().optional(),
  is_deletable: z.boolean().optional(),
});
export const claimsSchema = z.array(claimSchema);
export type Claim = z.infer<typeof claimSchema>;

export const clientSchema = z.object({
  id: z.string().nullish(),
  client_type: z.enum(['org', 'house']),
  name: z.string().nullable(),
  party_type_id: z.string().nullish(),
  tax_number: z.string().nullable(),
  registration_number: z.string().nullish(),
  address: z.object({
    country_iso: z.string().length(2).nullable(),
    postcode: z.string().nullable(),
    settlement: z.string().nullable(),
    street: z.string().nullable(),
  }),
  birth_date: z.union([z.string(), z.date()]).nullable().default(null),
  birth_place: z.string().nullish(),
  mother_name: z.string().nullish(),
  representative: z.object({
    name: z.string().nullable(),
    address: z.object({
      country_iso: z.literal('HU').nullable(),
      // country_iso: z.string().nullable(),
      postcode: z.string().nullable(),
      settlement: z.string().nullable(),
      street: z.string().nullable(),
    }),
  }).nullish(),
});
export type Client = z.infer<typeof clientSchema>;

export const bankAccountSchema = z.object({
  id: z.string().nullish(),
  iban: z.string().nullable(),
  account_number: z.string().nullable(),
});
export type BankAccount = z.infer<typeof bankAccountSchema>;

export type PaymentStepForm = 'debtor_form'
  | 'start_case_price_added_form'
  | 'interest_added_form'
  | 'flat_rate_cost_added_form'
  | typeof COFACE_FORM_NAME;

export const productTypes = [
  'notice',
  'demand_letter',
  'hard_1',
  'hard_2',
  'coface',
] as const;
export type ProductType = typeof productTypes[number];
type ProductFee = {
  label: string;
  details_text: string;
  currency_iso: string;
  amount: number | null;
  min: number | null;
  max: number | null;
  percentage: number | null;
};

const cofaceClaimSchema = z.object({
  id: z.string(),
  due_date_at: z.string().or(z.date()),
  original_amount: z.number(),
  currency_iso: z.string(),

  file: z.object({
    file_name: z.string(),
    storage_url: z.string(),
  }).or(z.instanceof(File)).nullish(),
  issued_at: z.union([z.string(), z.date()]).nullish().catch(null),
  coface_invoice_type_code: z.string().nullish().catch(null),
});
export const cofaceClaimsSchema = z.array(cofaceClaimSchema);
export type CofaceClaim = z.infer<typeof cofaceClaimSchema>;

export type Summary = {
  client: {
    name: string;
    terms_accepted_at: string | null;
  };
  debtor: {
    name: string;
    email: string;
    party_type_id: string | null;
    is_debtor_party_type_valid: boolean;
    coface_party_types: string[];
  };
  claims: CofaceClaim[];
  coface_invoice_types: {
    code: string;
    label: string;
  }[];
  payee_case: {
    displayed_claims_sums: ClaimSums;
    claims_sums: ClaimSums;
    currency_iso: string;
    is_interest_added: boolean;
    interest_amount_claims_sum: ClaimSums | null;
    is_flat_rate_cost_claim_added: boolean;
    flat_rate_cost_claims_sum: number | null;
    has_claims_eligible_for_flat_rate_cost: boolean;
    is_start_case_price_claim_added: boolean;
  };
  active_products: ProductType[];
  product_unavailable: {
    title: string;
    description: string;
    reasons: Partial<Record<ProductType, string[]>>;
  };
  product: {
    is_product_type_editable: boolean,
    type: ProductType;
    hard_2_price: {
      min: number | null;
      max: number | null;
      amount: number | null;
    };
    forms: {
      notice: PaymentStepForm[];
      demand_letter: PaymentStepForm[];
      hard_1: PaymentStepForm[];
      hard_2: PaymentStepForm[];
      coface: PaymentStepForm[];
    };
  };
  fees: {
    start: ProductFee[];
    'closed-success': ProductFee[];
  };
};

export const userEmailSchema = z.object({
  id: z.string().nullish(),
  email: z.string(),
  password: z.string().nullish(),
  terms_of_service: z.boolean().default(false),
});
export type UserEmail = z.infer<typeof userEmailSchema>;

// Szamlazz.hu
const SZAMLAZZHU_INVALID_TYPE_ERROR = 'INVALID_TYPE';
const SZAMLAZZHU_REMAINING_AMOUNT_MIN_ERROR = 'REMAINING_AMOUNT_MIN';
const SZAMLAZZHU_REMAINING_AMOUNT_MAX_ERROR = 'REMAINING_AMOUNT_MAX';
const SZAMLAZZHU_INVALID_CURRENCY_ERROR = 'INVALID_CURRENCY';
const SZAMLAZZHU_DUE_DATE_AT_NOT_PAST_ERROR = 'DUE_DATE_AT_MAX';
const SZAMLAZZHU_HAS_ONGOING_CASE_ERROR = 'HAS_ONGOING_CASE';
const SZAMLAZZHU_UNKNOWN_ERROR = 'UNKNOWN';
export const szamlazzhuInvoiceSchema = z.object({
  invoice_id: z.string(),
  invoice_number: z.string(),
  due_date_at: z.string(),
  remaining_amount: z.number().int(),
  currency_iso: z.string(),
  total_remaining_amount: z.number().int(),
  file: z.object({
    file_name: z.string(),
    storage_url: z.string().url(),
  }).or(z.instanceof(File)).nullable(),
  corrections: z.array(z.object({
    invoice_number: z.string(),
    due_date_at: z.string(),
    remaining_amount: z.number().int(),
    currency_iso: z.string(),
  })),
  error_code: z.enum([
    SZAMLAZZHU_INVALID_TYPE_ERROR,
    SZAMLAZZHU_REMAINING_AMOUNT_MIN_ERROR,
    SZAMLAZZHU_REMAINING_AMOUNT_MAX_ERROR,
    SZAMLAZZHU_INVALID_CURRENCY_ERROR,
    SZAMLAZZHU_DUE_DATE_AT_NOT_PAST_ERROR,
    SZAMLAZZHU_HAS_ONGOING_CASE_ERROR,
    SZAMLAZZHU_UNKNOWN_ERROR,
  ]).nullable(),
});
export const szamlazzhuInvoicesSchema = z.array(szamlazzhuInvoiceSchema);
export type SzamlazzhuInvoice = z.infer<typeof szamlazzhuInvoiceSchema>;

export const campaignSchema = z.object({
  utm_source: z.string().nullish(),
  utm_campaign: z.string().nullish(),
  utm_content: z.string().nullish(),
  utm_medium: z.string().nullish(),
});
export type Campaign = z.infer<typeof campaignSchema>;
